import React, { useState } from "react";
import { Gem, Users, Timer, Wallet, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { formatEther } from "viem";
import { useCollections } from "../../../hooks/useCollections";
import { useUserData } from "../../../hooks/useUserData";
import { useStakingContract } from "../../../hooks/useStakingContract";
import { CollectionCard } from "../collections/CollectionCard";
import { SalesActivity } from "../collections/SalesActivity";
import { StatCard } from "../../shared/StatCard";
import { useAccount } from "wagmi";
import { ConnectButton } from "../../ConnectButton";
import { cn } from "../../../lib/utils";

export function Dashboard() {
  const { data: collections, isLoading: isCollectionsLoading } = useCollections();
  const { data: userData, isLoading: isLoadingUserData } = useUserData();
  const { data: stakingData, isLoading: isLoadingStakingData } = useStakingContract();
  const { isConnected } = useAccount();
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Early return for loading state
  if (isCollectionsLoading || isLoadingUserData || isLoadingStakingData) {
    return (
      <div className="max-w-[1920px] mx-auto px-6 space-y-6">
        <div className="bg-gradient-to-br from-[#0054fa]/10 via-[#002787]/10 to-[#030a21]/80 backdrop-blur-xl border border-blue-500/20 rounded-2xl p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
            <StatCard title="Your Balance" value="Loading..." icon={Wallet} isLoading={true} />
            <StatCard title="Active Miners" value="Loading..." icon={Users} isLoading={true} />
            <StatCard title="Total Rewards" value="Loading..." icon={Gem} isLoading={true} />
          </div>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-36 bg-blue-500/20 rounded" />
          <div className="h-48 bg-blue-500/10 rounded-2xl" />
        </div>
      </div>
    );
  }

  // Handle error or no data state
  if (!collections || !userData || !stakingData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">No data available</p>
      </div>
    );
  }

  const selectedCollectionData = selectedCollection 
    ? collections.find(c => c.address === selectedCollection)
    : collections[0];

  return (
    <div className="max-w-[1920px] mx-auto px-6 pb-12">
      {/* Main Content */}
      <div className="flex gap-6">
        {/* Left Side - Collections Menu and Stats */}
        <div className="flex flex-col gap-6">
          {/* Toggle Button */}
          <motion.button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-3 bg-gradient-to-br from-[#0054fa]/20 via-[#002787]/20 to-[#030a21]/90 hover:from-[#0054fa]/30 hover:via-[#002787]/30 hover:to-[#030a21] border border-blue-500/30 rounded-xl transition-all duration-300 shadow-lg hover:shadow-blue-500/20"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              animate={{ rotate: isSidebarOpen ? 180 : 0 }}
              transition={{ duration: 0.3, ease: "anticipate" }}
            >
              {isSidebarOpen ? (
                <X className="w-5 h-5 text-blue-400" />
              ) : (
                <Menu className="w-5 h-5 text-blue-400" />
              )}
            </motion.div>
          </motion.button>

          {/* Sidebar */}
          <AnimatePresence mode="popLayout">
            {isSidebarOpen && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ 
                  width: "200px", 
                  opacity: 1,
                  transition: {
                    width: { duration: 0.3, ease: "anticipate" },
                    opacity: { duration: 0.2, delay: 0.1 }
                  }
                }}
                exit={{ 
                  width: 0, 
                  opacity: 0,
                  transition: {
                    width: { duration: 0.3, ease: "anticipate" },
                    opacity: { duration: 0.2 }
                  }
                }}
                className="bg-gradient-to-br from-[#0054fa]/10 via-[#002787]/10 to-[#030a21]/80 backdrop-blur-xl border border-blue-500/20 rounded-xl p-3 overflow-hidden"
              >
                <motion.h3 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-base font-bold text-white mb-3 px-2"
                >
                  Collections
                </motion.h3>
                <motion.div 
                  className="space-y-1.5"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.05
                      }
                    }
                  }}
                >
                  {collections.map((collection, index) => (
                    <motion.button
                      key={collection.address}
                      variants={{
                        hidden: { opacity: 0, x: -20 },
                        visible: { opacity: 1, x: 0 }
                      }}
                      onClick={() => setSelectedCollection(collection.address)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-lg transition-all duration-300",
                        selectedCollection === collection.address
                          ? "bg-blue-500/20 text-white shadow-md shadow-blue-500/10"
                          : "text-gray-400 hover:bg-blue-500/10 hover:text-white"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <img
                          src={collection.image}
                          alt={collection.name}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                        <span className="text-sm font-medium truncate">{collection.name}</span>
                      </div>
                    </motion.button>
                  ))}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Center - Collection Details and Stats */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Stats Grid */}
          {!isConnected ? (
            <div className="flex flex-col items-center justify-center gap-4 bg-blue-500/10 rounded-xl p-6">
              <p className="text-gray-400 text-sm text-center">Connect your wallet to view your balance and start tracking your NFTs</p>
              <ConnectButton />
            </div>
          ) : (
            <div className="bg-gradient-to-br from-[#0054fa]/10 via-[#002787]/10 to-[#030a21]/80 backdrop-blur-xl rounded-xl p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard
                  title="Your Balance"
                  value={`${Math.floor(Number(formatEther(userData.balance)))} $GEM`}
                  icon={Wallet}
                />
                <StatCard
                  title="Active Miners"
                  value={stakingData.participants.toString()}
                  icon={Users}
                />
                <StatCard
                  title="Total Rewards"
                  value={`${Math.floor(Number(formatEther(stakingData.rewardPoolSize)))} $GEM`}
                  icon={Gem}
                />
              </div>
            </div>
          )}

          {/* Selected Collection Card */}
          <AnimatePresence mode="wait">
            {selectedCollectionData && (
              <motion.div
                key={selectedCollectionData.address}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="w-full"
              >
                <CollectionCard
                  collection={selectedCollectionData}
                  index={0}
                  className="w-full"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right Side - Sales Activity */}
        <div className="w-[380px] flex-shrink-0">
          <SalesActivity />
        </div>
      </div>
    </div>
  );
}